import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Une erreur s\'est produite. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Hero Section avec couleurs maquette */}
      <section className="relative bg-gradient-to-r from-maquette-blue to-maquette-dark text-white py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="contact-title">
              {language === 'fr' && 'Contactez-nous'}
              {language === 'en' && 'Contact us'}
              {language === 'ar' && 'اتصل بنا'}
            </h1>
            <p className="text-xl md:text-2xl text-gray-100">
              {language === 'fr' && 'Nous sommes là pour répondre à toutes vos questions'}
              {language === 'en' && 'We are here to answer all your questions'}
              {language === 'ar' && 'نحن هنا للإجابة على جميع أسئلتك'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-maquette-text mb-8">
                {language === 'fr' && 'Informations de contact'}
                {language === 'en' && 'Contact information'}
                {language === 'ar' && 'معلومات الاتصال'}
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                  <div className="bg-maquette-blue/10 rounded-full p-3">
                    <MapPin className="text-maquette-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-maquette-text mb-2">
                      {language === 'fr' && 'Adresse'}
                      {language === 'en' && 'Address'}
                      {language === 'ar' && 'العنوان'}
                    </h3>
                    <p className="text-maquette-gray">Muscat, Oman</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                  <div className="bg-maquette-blue/10 rounded-full p-3">
                    <Phone className="text-maquette-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-maquette-text mb-2">
                      {language === 'fr' && 'Téléphone'}
                      {language === 'en' && 'Phone'}
                      {language === 'ar' && 'الهاتف'}
                    </h3>
                    <a href="tel:+96617343878" className="text-maquette-gray hover:text-maquette-blue transition-colors">
                      +966 1734 3878
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md">
                  <div className="bg-maquette-blue/10 rounded-full p-3">
                    <Mail className="text-maquette-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-maquette-text mb-2">Email</h3>
                    <a href="mailto:info@hotcomconnectes.om" className="text-maquette-gray hover:text-maquette-blue transition-colors">
                      info@hotcomconnectes.om
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-maquette-text mb-3">
                  {language === 'fr' && 'Horaires d\'ouverture'}
                  {language === 'en' && 'Opening hours'}
                  {language === 'ar' && 'ساعات العمل'}
                </h3>
                <div className="space-y-2 text-maquette-gray">
                  <p><span className="font-semibold">
                    {language === 'fr' && 'Lundi - Vendredi:'}
                    {language === 'en' && 'Monday - Friday:'}
                    {language === 'ar' && 'الإثنين - الجمعة:'}
                  </span> 9h00 - 18h00</p>
                  <p><span className="font-semibold">
                    {language === 'fr' && 'Samedi:'}
                    {language === 'en' && 'Saturday:'}
                    {language === 'ar' && 'السبت:'}
                  </span> 10h00 - 16h00</p>
                  <p><span className="font-semibold">
                    {language === 'fr' && 'Dimanche:'}
                    {language === 'en' && 'Sunday:'}
                    {language === 'ar' && 'الأحد:'}
                  </span> 
                    {language === 'fr' && 'Fermé'}
                    {language === 'en' && 'Closed'}
                    {language === 'ar' && 'مغلق'}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-3xl font-bold text-maquette-text mb-6">
                {language === 'fr' && 'Envoyez-nous un message'}
                {language === 'en' && 'Send us a message'}
                {language === 'ar' && 'أرسل لنا رسالة'}
              </h2>
              
              {submitSuccess && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3" data-testid="success-message">
                  <CheckCircle className="text-green-600" size={24} />
                  <p className="text-green-800 font-medium">
                    {language === 'fr' && 'Votre message a été envoyé avec succès ! Nous vous répondrons bientôt.'}
                    {language === 'en' && 'Your message has been sent successfully! We will respond soon.'}
                    {language === 'ar' && 'تم إرسال رسالتك بنجاح! سنرد قريباً.'}
                  </p>
                </div>
              )}

              {submitError && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
                  <p className="text-red-800 font-medium">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-maquette-text font-medium mb-2">
                      {language === 'fr' && 'Nom complet *'}
                      {language === 'en' && 'Full name *'}
                      {language === 'ar' && 'الاسم الكامل *'}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                      placeholder={language === 'fr' ? 'Votre nom' : language === 'en' ? 'Your name' : 'اسمك'}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-maquette-text font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-maquette-text font-medium mb-2">
                      {language === 'fr' && 'Téléphone'}
                      {language === 'en' && 'Phone'}
                      {language === 'ar' && 'الهاتف'}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                      placeholder="+966 xxx xxxx"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-maquette-text font-medium mb-2">
                      {language === 'fr' && 'Sujet *'}
                      {language === 'en' && 'Subject *'}
                      {language === 'ar' && 'الموضوع *'}
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      data-testid="input-subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue"
                    >
                      <option value="">
                        {language === 'fr' && 'Sélectionnez un sujet'}
                        {language === 'en' && 'Select a subject'}
                        {language === 'ar' && 'اختر موضوعاً'}
                      </option>
                      <option value="voyages">
                        {language === 'fr' && 'Voyages'}
                        {language === 'en' && 'Travel'}
                        {language === 'ar' && 'السفر'}
                      </option>
                      <option value="it-services">
                        {language === 'fr' && 'Services IT'}
                        {language === 'en' && 'IT Services'}
                        {language === 'ar' && 'خدمات تقنية المعلومات'}
                      </option>
                      <option value="devis">
                        {language === 'fr' && 'Demande de devis'}
                        {language === 'en' && 'Quote request'}
                        {language === 'ar' && 'طلب عرض سعر'}
                      </option>
                      <option value="autre">
                        {language === 'fr' && 'Autre'}
                        {language === 'en' && 'Other'}
                        {language === 'ar' && 'آخر'}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-maquette-text font-medium mb-2">
                      {language === 'fr' && 'Message *'}
                      {language === 'en' && 'Message *'}
                      {language === 'ar' && 'الرسالة *'}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-maquette-blue resize-none"
                      placeholder={language === 'fr' ? 'Décrivez votre projet ou votre demande...' : language === 'en' ? 'Describe your project or request...' : 'صف مشروعك أو طلبك...'}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-maquette-dark text-white px-8 py-4 rounded-md font-semibold hover:bg-maquette-blue transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <span>{isSubmitting ? 
                      (language === 'fr' ? 'Envoi en cours...' : language === 'en' ? 'Sending...' : 'جاري الإرسال...') 
                      : 
                      (language === 'fr' ? 'Envoyer le message' : language === 'en' ? 'Send message' : 'إرسال الرسالة')
                    }</span>
                    <Send size={20} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-maquette-text mb-8 text-center">
              {language === 'fr' && 'Nous trouver'}
              {language === 'en' && 'Find us'}
              {language === 'ar' && 'اعثر علينا'}
            </h2>
            <div className="bg-gray-200 rounded-xl overflow-hidden shadow-lg" style={{height: '400px'}}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d233117.75915028456!2d58.18270964999999!3d23.583333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8dfb38f46b7c77%3A0xfc5c14e0ae64c613!2sMuscat%2C%20Oman!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="100%"
                style={{border: 0}}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map of Muscat, Oman"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;